import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { type AdminAuditRow, useAdminAudit, useAdminHive, useAdminOverview } from '../lib/admin';
import { usePagedQuery } from '../lib/paged';
import { useMe } from '../lib/session';
import { AdminAuditTail } from './AdminAuditTail';
import { AdminHiveBand } from './AdminHiveBand';
import { AdminStatBand } from './AdminStatBand';
import { AdminTrendBands } from './AdminTrendBands';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { SessionBadge } from './SessionBadge';
import { Skeleton } from './Skeleton';

// Platform radar container. Mounts only for staff (behind <AdminGate/>), so its
// `/v1/admin/overview` + `/v1/admin/audit` reads never fire for an outsider.
// The bands + audit tail are pure presenters (AdminTrendBands / AdminStatBand /
// AdminAuditTail).
//
// The tail PAGES. `/v1/admin/audit` mints a real keyset cursor, so it goes
// through `usePagedQuery` like the two registries next door: the tail is a window
// on a table that only grows, and page one was previously the whole surface an
// operator could reach — a fifty-first row simply did not exist for them.

export function AdminOverviewPanel(props: { token: string | null }): JSX.Element {
  const me = useMe(props.token);
  const overview = useAdminOverview(props.token);
  const hive = useAdminHive(props.token);
  const audit = usePagedQuery<AdminAuditRow>((cursor) => useAdminAudit(cursor, 50, props.token));
  /** The tail links a row's session only on an org the operator belongs to (`AdminAuditTail`). */
  const isMember = (login: string): boolean =>
    (me.data?.memberships ?? []).some((m) => m.github_login === login);

  return (
    <>
      <PageHeader
        title={t('admin.overview_title')}
        desc={t('admin.overview_subtitle')}
        actions={<SessionBadge login={me.data?.github_login ?? null} loading={me.isLoading} />}
      />

      <QueryBoundary query={overview} loading={<Skeleton height="8rem" />}>
        {(data) => (
          <>
            <AdminTrendBands overview={data} />
            <AdminStatBand overview={data} />
          </>
        )}
      </QueryBoundary>

      {/* The hive band (plan 102 slice 10). It sits on the PLATFORM radar and not on an
          org page because it grades the fleet's own producers: a lane that emitted twice
          this week looks silent in every tenant it did not emit in. */}
      <QueryBoundary query={hive} loading={<Skeleton height="8rem" />}>
        {(data) => <AdminHiveBand hive={data} />}
      </QueryBoundary>

      <Panel title={t('admin.audit_heading')}>
        <QueryBoundary
          query={audit.view}
          isEmpty={(rows) => rows.length === 0}
          loading={
            <div class="admin__skeletons">
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
            </div>
          }
          empty={<EmptyState message={t('admin.audit_empty')} />}
        >
          {(rows) => (
            <>
              <AdminAuditTail rows={rows} isMember={isMember} />
              <LoadMore list={audit} label={t('admin.load_more')} />
            </>
          )}
        </QueryBoundary>
      </Panel>
    </>
  );
}
